import { Lock } from '@phosphor-icons/react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { ApiError } from '@/lib/api/errors'
import { friendlyErrorKey, serverMessage } from '@/lib/api/friendly-errors'
import { kuniriIconWeight } from '@/lib/icons'

import {
  activityDetailApi,
  type ActivityDetail,
  type Participant,
  type PrivateProfile,
  type ReportReason,
} from '../api'
import {
  canViewParticipants,
  canViewSensitiveFields,
  shareableInviteToken,
} from '../access'
import { ActionPanel } from '../components/action-panel'
import { CreatorActions } from '../components/creator-actions'
import { ActivityCover } from '../components/activity-cover'
import { ActivityOverview } from '../components/activity-overview'
import { FeedbackNotice, type ActivityNotice } from '../components/feedback-notice'
import { FeedbackPanel } from '../components/feedback-panel'
import { JoinRequestsPanel } from '../components/join-requests-panel'
import { LocationSection } from '../components/location-section'
import {
  ParticipantsPanel,
  type ParticipantsStatus,
} from '../components/participants-panel'
import { ShareSheet } from '../components/share-sheet'
import { activityShareUrl } from '../links'

type DetailState =
  | { status: 'loading' }
  | { status: 'ready'; activity: ActivityDetail }
  | { status: 'unavailable' }
  | { status: 'error'; error: unknown }

interface ParticipantsState {
  status: ParticipantsStatus
  items: Participant[]
}

type NoticeState = ActivityNotice

interface ActivityDetailViewProps {
  activityId: string | null
  inviteToken: string | null
  onRetry: () => void
}

/**
 * Read-only activity detail (UI_PARITY §3.9, slice 2). Deep links keep
 * `?invite=` through auth (RequireAuth) and forward it to detail/participants.
 *
 * Server state is owned per route lifecycle with an abortable request, not a
 * client cache (D-042). The outer screen remounts the view on route/query or
 * retry changes, so request state starts clean without synchronous setters in
 * effects. Join/leave/chat are deliberately placeholders until slice 3; this
 * screen only calls endpoints that exist in the frozen contract.
 */
export function ActivityDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [reloadKey, setReloadKey] = useState(0)

  const rawInvite = searchParams.get('invite')
  const inviteToken = rawInvite?.trim() ? rawInvite.trim() : null
  const activityId = id?.trim() ? id : null

  return (
    <ActivityDetailView
      key={`${location.key}:${reloadKey}`}
      activityId={activityId}
      inviteToken={inviteToken}
      onRetry={() => setReloadKey((key) => key + 1)}
    />
  )
}

/**
 * TASK-4 (maintainer): mirrors the server rule — a join without a picture is
 * rejected, so the sidebar disables the CTA before the request.
 */
function profileIncompleteForViewer(profile: {
  profileCompleteForParticipation: boolean
} | null): boolean {
  return profile !== null && !profile.profileCompleteForParticipation
}

function ActivityDetailView({
  activityId,
  inviteToken,
  onRetry,
}: ActivityDetailViewProps) {
  const { t } = useTranslation()

  const [detail, setDetail] = useState<DetailState>({ status: 'loading' })
  const [participants, setParticipants] = useState<ParticipantsState>({
    status: 'loading',
    items: [],
  })
  // TASK-4: the join gate needs this client-side mirror of `getMe`.
  const [viewerProfile, setViewerProfile] = useState<PrivateProfile | null>(
    null,
  )
  const [saved, setSaved] = useState<boolean | null>(null)
  const [saveBusy, setSaveBusy] = useState(false)
  const [joinBusy, setJoinBusy] = useState(false)
  const [leaveBusy, setLeaveBusy] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  const [stopBusy, setStopBusy] = useState(false)
  const [notice, setNotice] = useState<NoticeState | null>(null)
  const [shareOpen, setShareOpen] = useState(false)
  const [participantsRetryKey, setParticipantsRetryKey] = useState(0)
  const [detailReloadKey, setDetailReloadKey] = useState(0)

  useEffect(() => {
    if (!activityId) return

    const controller = new AbortController()

    void activityDetailApi
      .getActivity(activityId, inviteToken, { signal: controller.signal })
      .then((activity) => {
        if (controller.signal.aborted) return
        setDetail({ status: 'ready', activity })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (error instanceof ApiError && error.status === 404) {
          setDetail({ status: 'unavailable' })
          return
        }
        setDetail({ status: 'error', error })
      })

    return () => controller.abort()
  }, [activityId, inviteToken, detailReloadKey])

  useEffect(() => {
    const controller = new AbortController()
    void activityDetailApi
      .getViewerProfile({ signal: controller.signal })
      .then((profile) => {
        if (controller.signal.aborted) return
        setViewerProfile(profile)
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setViewerProfile(null)
      })
    return () => controller.abort()
  }, [detailReloadKey])


  useEffect(() => {
    if (detail.status !== 'ready') return
    const { activity } = detail

    if (!canViewParticipants(activity, inviteToken)) return

    const controller = new AbortController()

    void activityDetailApi
      .listParticipants(activity.id, inviteToken, {
        signal: controller.signal,
      })
      .then((response) => {
        if (!controller.signal.aborted) {
          setParticipants({ status: 'ready', items: response.items })
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (
          error instanceof ApiError &&
          (error.status === 403 || error.code === 'FORBIDDEN')
        ) {
          setParticipants({ status: 'restricted', items: [] })
          return
        }
        setParticipants({ status: 'error', items: [] })
      })

    return () => controller.abort()
  }, [detail, inviteToken, participantsRetryKey])

  const closeShare = useCallback(() => setShareOpen(false), [])

  const handleParticipantsRetry = useCallback(() => {
    setParticipants({ status: 'loading', items: [] })
    setParticipantsRetryKey((key) => key + 1)
  }, [])

  const visibleDetail: DetailState = activityId
    ? detail
    : { status: 'unavailable' }
  const participantsRestricted =
    visibleDetail.status === 'ready' &&
    !canViewParticipants(visibleDetail.activity, inviteToken)
  const participantsView: ParticipantsState = participantsRestricted
    ? { status: 'restricted', items: [] }
    : participants
  const ready =
    visibleDetail.status === 'ready' ? visibleDetail.activity : null
  const canViewSensitive = ready ? canViewSensitiveFields(ready) : false
  const shareToken = ready ? shareableInviteToken(ready, inviteToken) : null
  const canShare = Boolean(
    ready &&
      (ready.participationMode !== 'invite' || Boolean(shareToken)),
  )
  const savedValue = saved ?? ready?.saved ?? false

  const showNotice = useCallback((next: ActivityNotice) => {
    setNotice(next)
  }, [])

  const refreshDetail = useCallback(() => {
    setDetailReloadKey((key) => key + 1)
  }, [])

  const refreshParticipants = useCallback(() => {
    setParticipantsRetryKey((key) => key + 1)
  }, [])

  const refreshAfterDecision = useCallback(() => {
    refreshDetail()
    refreshParticipants()
  }, [refreshDetail, refreshParticipants])

  const handleJoin = useCallback(async (): Promise<boolean> => {
    if (!ready || joinBusy) return false
    setJoinBusy(true)
    setNotice(null)
    try {
      const invite =
        ready.participationMode === 'invite'
          ? (inviteToken ?? ready.inviteToken)
          : null
      const response = await activityDetailApi.join(ready.id, invite)
      setNotice({
        tone: 'success',
        key:
          response.status === 'waitlisted'
            ? 'activityDetail.join.successWaitlist'
            : response.status === 'pending'
              ? 'activityDetail.join.successPending'
              : 'activityDetail.join.successJoined',
      })
      refreshDetail()
      return true
    } catch (error) {
      setNotice({
        tone: 'error',
        key: friendlyErrorKey(error),
        message: serverMessage(error) ?? undefined,
      })
      return false
    } finally {
      setJoinBusy(false)
    }
  }, [ready, joinBusy, inviteToken, refreshDetail])

  const handleLeave = useCallback(async (): Promise<boolean> => {
    if (!ready || leaveBusy) return false
    const previousStatus = ready.viewerStatus
    setLeaveBusy(true)
    setNotice(null)
    try {
      await activityDetailApi.leave(ready.id)
      setNotice({
        tone: 'success',
        key:
          previousStatus === 'waitlisted'
            ? 'activityDetail.leave.successWaitlist'
            : previousStatus === 'pending'
              ? 'activityDetail.leave.successPending'
              : 'activityDetail.leave.successJoined',
      })
      refreshDetail()
      return true
    } catch (error) {
      setNotice({
        tone: 'error',
        key: friendlyErrorKey(error),
        message: serverMessage(error) ?? undefined,
      })
      return false
    } finally {
      setLeaveBusy(false)
    }
  }, [ready, leaveBusy, refreshDetail])

  const handleCancelActivity = useCallback(async (): Promise<boolean> => {
    if (!ready || cancelBusy) return false
    setCancelBusy(true)
    setNotice(null)
    try {
      await activityDetailApi.cancel(ready.id)
      setNotice({
        tone: 'success',
        key: 'activityDetail.creatorActions.cancelSuccess',
      })
      refreshDetail()
      return true
    } catch (error) {
      setNotice({
        tone: 'error',
        key: friendlyErrorKey(error),
        message: serverMessage(error) ?? undefined,
      })
      return false
    } finally {
      setCancelBusy(false)
    }
  }, [ready, cancelBusy, refreshDetail])

  const handleStopRecurrence = useCallback(async (): Promise<boolean> => {
    if (!ready || stopBusy) return false
    setStopBusy(true)
    setNotice(null)
    try {
      await activityDetailApi.stopRecurrence(ready.id)
      setNotice({
        tone: 'success',
        key: 'activityDetail.recurrence.stopped',
      })
      refreshDetail()
      return true
    } catch (error) {
      setNotice({
        tone: 'error',
        key: friendlyErrorKey(error),
        message: serverMessage(error) ?? undefined,
      })
      return false
    } finally {
      setStopBusy(false)
    }
  }, [ready, stopBusy, refreshDetail])

  const toggleSave = useCallback(async () => {
    if (!ready || saveBusy) return
    const nextSaved = !savedValue
    setSaveBusy(true)
    setNotice(null)
    try {
      const response = nextSaved
        ? await activityDetailApi.save(ready.id)
        : await activityDetailApi.unsave(ready.id)
      setSaved(response.saved)
      setNotice({
        tone: 'success',
        key: response.saved
          ? 'activityDetail.actions.saveSuccess'
          : 'activityDetail.actions.unsaveSuccess',
      })
    } catch (error) {
      setNotice({
        tone: 'error',
        key: friendlyErrorKey(error),
        message: serverMessage(error) ?? undefined,
      })
    } finally {
      setSaveBusy(false)
    }
  }, [ready, saveBusy, savedValue])

  const handleShare = useCallback(async () => {
    if (!ready) return
    const url = activityShareUrl(ready.id, shareToken)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: ready.title, url })
        return
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
      }
    }
    setShareOpen(true)
  }, [ready, shareToken])

  const handleReport = useCallback(
    async (reason: ReportReason, description: string) => {
      if (!ready) return
      await activityDetailApi.report({
        targetType: 'activity',
        targetId: ready.id,
        reason,
        ...(description ? { description } : {}),
      })
      setNotice({ tone: 'success', key: 'activityDetail.actions.reportSuccess' })
    },
    [ready],
  )

  const handleBlock = useCallback(async () => {
    if (!ready) return
    await activityDetailApi.blockUser(ready.creatorId)
    setNotice({ tone: 'success', key: 'activityDetail.actions.blockSuccess' })
    setShareOpen(false)
    setDetail({ status: 'unavailable' })
  }, [ready])

  if (visibleDetail.status === 'loading') {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="grid min-h-dvh place-items-center bg-paper px-4"
      >
        <h1 className="sr-only">{t('screens.activity.title')}</h1>
        <LoadingState labelKey="activityDetail.loading" />
      </main>
    )
  }

  if (visibleDetail.status === 'unavailable') {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 py-10 md:py-14"
      >
        <div className="mx-auto max-w-[640px] space-y-5">
          <Link to="/activities" className="btn-ghost px-0">
            {t('activityDetail.back')}
          </Link>
          {notice ? (
            <FeedbackNotice tone={notice.tone}>{notice.message ?? t(notice.key)}</FeedbackNotice>
          ) : null}
          <div className="rounded-card border border-line bg-white p-6 text-center">
            <span className="mx-auto grid size-14 place-items-center rounded-full bg-mint text-teal">
              <Lock size={26} weight={kuniriIconWeight} aria-hidden="true" />
            </span>
            <h1 className="mt-4 text-[clamp(1.7rem,4vw,2.2rem)]">
              {t('activityDetail.unavailableTitle')}
            </h1>
            <p className="mx-auto mt-2 max-w-[52ch] text-[15px] leading-relaxed text-muted">
              {t('activityDetail.unavailableBody')}
            </p>
            <Link to="/activities" className="btn-primary mt-6">
              {t('activityDetail.back')}
            </Link>
          </div>
        </div>
      </main>
    )
  }

  if (visibleDetail.status === 'error') {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 py-10 md:py-14"
      >
        <div className="mx-auto max-w-[640px] space-y-5">
          <Link to="/activities" className="btn-ghost px-0">
            {t('activityDetail.back')}
          </Link>
          <h1 className="sr-only">{t('screens.activity.title')}</h1>
          <ErrorState
            error={visibleDetail.error}
            titleKey="activityDetail.loadErrorTitle"
            onRetry={onRetry}
            retryLabelKey="activityDetail.retry"
          />
        </div>
      </main>
    )
  }

  if (!ready) return null

  return (
    <main id="main-content" tabIndex={-1} className="min-h-dvh bg-paper">
      <ActivityCover
        activity={ready}
        canViewSensitive={canViewSensitive}
        canShare={canShare}
        onShare={() => void handleShare()}
      />

      <div className="mx-auto w-full max-w-[1240px] px-4 pt-6 pb-[calc(1.5rem+var(--safe-bottom))] md:px-9 md:pt-8 md:pb-8">
        <div className="grid grid-cols-1 gap-5 min-[960px]:grid-cols-[minmax(0,1fr)_350px] min-[960px]:gap-8">
          <div className="min-w-0 min-[960px]:col-start-1 min-[960px]:row-start-1">
            <ActivityOverview
              activity={ready}
              canViewSensitive={canViewSensitive}
            />
          </div>

          <div className="min-w-0 min-[960px]:col-start-2 min-[960px]:row-span-2 min-[960px]:row-start-1">
            {notice ? (
              <FeedbackNotice tone={notice.tone} className="mb-3">
                {notice.message ?? t(notice.key)}
              </FeedbackNotice>
            ) : null}
            <ActionPanel
              activity={ready}
              profileIncomplete={profileIncompleteForViewer(viewerProfile)}
              saved={savedValue}
              saveBusy={saveBusy}
              joinBusy={joinBusy}
              leaveBusy={leaveBusy}
              canBlock={ready.viewerStatus !== 'creator'}
              canReport={ready.viewerStatus !== 'creator'}
              onToggleSave={() => void toggleSave()}
              onReport={handleReport}
              onBlock={handleBlock}
              onJoin={handleJoin}
              onLeave={handleLeave}
            />
          </div>

          <div className="min-w-0 space-y-5 min-[960px]:col-start-1 min-[960px]:row-start-2">
            <ParticipantsPanel
              activity={ready}
              status={participantsView.status}
              participants={participantsView.items}
              onRetry={handleParticipantsRetry}
              onChanged={refreshAfterDecision}
              onNotice={showNotice}
            />
            <LocationSection
              activity={ready}
              canViewSensitive={canViewSensitive}
            />
            <JoinRequestsPanel
              activity={ready}
              onChanged={refreshAfterDecision}
              onNotice={showNotice}
            />
            <FeedbackPanel
              activity={ready}
              participants={participantsView.items}
              participantsStatus={participantsView.status}
              onChanged={refreshParticipants}
              onNotice={showNotice}
            />
            {ready.viewerStatus === 'creator' ? (
              <CreatorActions
                activity={ready}
                cancelBusy={cancelBusy}
                stopBusy={stopBusy}
                onCancelActivity={handleCancelActivity}
                onStopRecurrence={handleStopRecurrence}
              />
            ) : null}
          </div>
        </div>
      </div>

      {shareOpen ? (
        <ShareSheet
          title={ready.title}
          url={activityShareUrl(ready.id, shareToken)}
          inviteOnly={ready.participationMode === 'invite'}
          onClose={closeShare}
        />
      ) : null}
    </main>
  )
}
